<script lang="ts">
  // Picking an OmniClass code, instead of typing one.
  //
  // The field is still free text and still the source of truth — plenty of authors know their code
  // and will type it, and a picker that refused an unlisted code would be wrong the day CSI publishes
  // a new one. What the list adds is the other 99%: nobody is going to recall "23.30.10.14.11" from
  // memory, so before this existed the field stayed empty and the catalog stayed unclassified.
  //
  // Two things make it usable rather than a 2645-row wall:
  //   • the family's own Revit category narrows it — Autodesk's taxonomy file maps most nodes to a
  //     BuiltInCategory, so a door family is offered ~90 codes instead of 2645;
  //   • matching is on the code AND the title, because an author knows the word "hinge", not a number.
  //
  // Revit-only, and it fails soft: in the browser (or on a machine whose Revit did not install the
  // taxonomy) the command is unavailable and this degrades to exactly the text box it replaced.

  import { onMount } from "svelte";
  import { transport } from "@/transport";
  import type { OmniClassNode, OmniClassTaxonomy } from "./types";
  import { t } from "@/i18n/index.svelte";
  import { AppInput } from "@/components/ui";

  let {
    code = $bindable(),
    title = $bindable(),
    category,
  }: {
    code: string;
    title: string | null;
    /** BuiltInCategory enum name of the family being published; drives the suggestions. */
    category: string | null;
  } = $props();

  let nodes = $state<OmniClassNode[]>([]);
  let taxonomyAvailable = $state(false);

  let open = $state(false);
  let query = $state("");
  let highlighted = $state(0);

  onMount(() => {
    transport
      .invoke<OmniClassTaxonomy>("GetOmniClassTaxonomy")
      .then((taxonomy) => {
        nodes = taxonomy.nodes ?? [];
        taxonomyAvailable = nodes.length > 0;
      })
      .catch(() => {
        // Not in Revit, or Revit installed no taxonomy. The input below still works.
        taxonomyAvailable = false;
      });
  });

  /** Codes Autodesk maps to this family's category — the short list worth showing first. */
  const suggested = $derived(category ? nodes.filter((n) => n.category === category) : []);

  /**
   * What the list shows. With nothing typed it is the suggestions, or the top of the tree when the
   * category maps to none — an empty list would read as "there are no codes".
   *
   * Capped, because rendering 2645 rows to show the first ten costs more than it informs.
   */
  const matches = $derived.by<OmniClassNode[]>(() => {
    const q = query.trim().toLowerCase();

    if (!q) {
      return (suggested.length ? suggested : nodes.filter((n) => n.code.split(".").length <= 2)).slice(0, 200);
    }

    const hit = (n: OmniClassNode) => n.code.startsWith(q) || n.title.toLowerCase().includes(q);

    // Suggestions first: when a door family's author types "panel", the door panels belong above the
    // curtain-wall ones.
    const inCategory = suggested.filter(hit);
    const inCategoryCodes = new Set(inCategory.map((n) => n.code));
    const rest = nodes.filter((n) => hit(n) && !inCategoryCodes.has(n.code));

    return [...inCategory, ...rest].slice(0, 200);
  });

  function choose(node: OmniClassNode) {
    code = node.code;
    title = node.title;
    query = "";
    open = false;
  }

  /** Typing in the field sets the code directly, and drops any title that no longer belongs to it. */
  function onInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    code = value;
    title = null;
    query = value;
    open = true;
    highlighted = 0;
  }

  function clear() {
    code = "";
    title = null;
    query = "";
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      open = false;
      return;
    }
    if (!open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      open = true;
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      highlighted = Math.min(highlighted + 1, matches.length - 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      highlighted = Math.max(highlighted - 1, 0);
    } else if (event.key === "Enter") {
      const node = matches[highlighted];
      // Only swallow Enter when it means something. Otherwise it belongs to the form.
      if (open && node) {
        event.preventDefault();
        choose(node);
      }
    }
  }
</script>

<label class="flex flex-col gap-1 relative">
  <span class="text-xs font-medium text-ink-muted">{t("publish.omniClass")}</span>

  <div class="flex items-center gap-1">
    <AppInput
      value={code}
      type="text"
      role="combobox"
      aria-expanded={open}
      aria-autocomplete="list"
      aria-controls="apibim-omniclass-listbox"
      placeholder={taxonomyAvailable ? t("publish.omniClassSearch") : t("publish.omniClassHint")}
      class="flex-1 min-w-0 font-mono"
      oninput={onInput}
      onfocus={() => (open = taxonomyAvailable)}
      onblur={() => (open = false)}
      onkeydown={onKeydown}
    />
    {#if code}
      <button
        type="button"
        class="shrink-0 px-2 py-2 text-xs text-ink-muted hover:text-error transition-colors"
        title={t("publish.omniClassClear")}
        onclick={clear}
      >
        ✕
      </button>
    {/if}
  </div>

  <!-- The title of whatever code is currently in the field, when we know it. It is the only way
       to tell "23.30.10.14" from "23.30.10.17" at a glance. -->
  {#if title}
    <span class="text-xs text-ink-muted">{title}</span>
  {/if}

  <!-- Clicking away closes the list. mousedown rather than click so the blur does not remove the
       row before the click lands on it. -->
  {#if open && taxonomyAvailable}
    <div
      id="apibim-omniclass-listbox"
      class="absolute z-20 top-full left-0 right-0 mt-1 max-h-64 overflow-auto rounded-[--brand-radius-control] border border-line bg-white shadow-[--brand-shadow-surface]"
    >
      {#if !matches.length}
        <p class="px-3 py-2 text-xs text-ink-muted">
          {t("publish.omniClassNoMatch")}
        </p>
      {:else if !query.trim() && suggested.length}
        <p class="px-3 py-1.5 text-[11px] uppercase tracking-wide text-ink-muted border-b border-line">
          {t("publish.omniClassForCategory")}
        </p>
      {/if}

      {#each matches as node, i (node.code)}
        <button
          type="button"
          class="w-full text-left px-3 py-1.5 flex items-baseline gap-2 hover:bg-panel {i === highlighted ? 'bg-panel' : ''}"
          onmousedown={(event) => {
            event.preventDefault();
            choose(node);
          }}
          onmouseenter={() => (highlighted = i)}
        >
          <span class="font-mono text-xs text-ink-muted shrink-0">{node.code}</span>
          <span class="text-sm truncate">{node.title}</span>
        </button>
      {/each}
    </div>
  {/if}
</label>
